import { Backdrop } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { format } from "date-fns";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAppDispatch } from "../../../../app/hooks";
import DialogContentWrapper from "../../../../common/components/functional/dialogs/DialogContentWrapper";
import LoadingSpinner from "../../../../common/components/functional/loadingSpinner/LoadingSpinner";
import {
  addTalentWorkHistoryEntry,
} from "../../../workHistory/WorkHistoriesSlice";
import { AddTalentWorkHistoryEntry } from "../../../workHistory/types/commands";
import CityControl from "./CityControl";
import DateControl from "./DateControl";
import JobDescriptionControl from "./JobDescriptionControl";
import WorkLocationControl from "./WorkLocationControl";
import WorkTitleControl from "./WorkTitleControl";
import { FormValuesAdd } from "./types";
import { UnsavedChangesProvider } from "../../../../contexts/UnsavedChangesContext";
import { isoFormat } from "../../../../common/helpers/formatDateTime";
import { EezyWorkControl } from "./EezyWorkControl";
import DayCountControl from "./DayCountControl";

type Props = {
  onClose: () => void;
};

const TalentAddWorkHistoryEntry = ({ onClose }: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { talentId } = useParams();

  const defaultValues: FormValuesAdd = {
    isEezyWork: false,
    workLocationName: "",
    workLocation: null,
    workTitle: null,
    jobDescription: "",
    city: "",
    startDate: null,
    endDate: null,
    dayCount: null,
  };

  const methods = useForm<FormValuesAdd>({
    defaultValues: defaultValues,
    mode: "onChange",
  });
  const isEezyWork = methods.watch("isEezyWork");

  const onSubmit = (formValues: FormValuesAdd) => {
    setIsLoading(true);

    const startDate = format(formValues.startDate as Date, isoFormat);
    const endDate = formValues.endDate
      ? format(formValues.endDate, isoFormat)
      : undefined;
    const workTitleName = formValues.workTitle?.name;

    if (talentId && workTitleName) {
      const commandPayload: AddTalentWorkHistoryEntry = {
        workHistory: {
          talentId,
          ...(formValues.isEezyWork
            ? {
                workLocationId: formValues.workLocation!!.id,
                workLocationName: formValues.workLocation!!.name,
                customerOrgId: formValues.workLocation!!.customerOrgId,
                customerOrgName: formValues.workLocation!!.customerOrgName,
              }
            : {
                workLocationName: formValues.workLocationName,
              }),
          workTitleId: formValues.workTitle?.id || null,
          workTitleName: workTitleName,
          ...(formValues.jobDescription && {
            jobDescription: formValues.jobDescription,
          }),
          city: formValues.city,
          startDate,
          endDate,
        },
        ...(formValues.isEezyWork &&
          formValues.dayCount && { dayCount: formValues.dayCount }),
      };
      dispatch(addTalentWorkHistoryEntry(commandPayload)).then((action) => {
        if (isFulfilled(action)) {
          onClose();
        }
        return isFulfilled(action);
      });
    }
  };

  const onSave = () => {
    methods.handleSubmit(onSubmit)();
  };

  const onCancel = () => {
    onClose();
  };

  const disabled =
    !methods.formState.isValid ||
    !methods.formState.isDirty ||
    isLoading;

  return (
    <FormProvider {...methods}>
      <DialogContentWrapper
        title={t("talentProfile:workExperience")}
        margin
        onSave={onSave}
        onCancel={onCancel}
        saveDisabled={disabled}
      >
        <Backdrop
          open={isLoading}
          sx={{
            backgroundColor: "rgba(255, 255, 255, 0.4)",
            zIndex: 1000,
          }}
        >
          <LoadingSpinner />
        </Backdrop>
        <UnsavedChangesProvider isDirty={methods.formState.isDirty}>
          <EezyWorkControl />
          <WorkLocationControl />
          <WorkTitleControl
            defaultWorkTitle={defaultValues.workTitle}
            requireDropdownSelectionForEezyWork
          />
          <JobDescriptionControl />
          <CityControl />
          {isEezyWork && <DayCountControl />}
          <DateControl
            start={defaultValues.startDate}
            end={defaultValues.endDate}
            allowOngoingToggle
          />
        </UnsavedChangesProvider>
      </DialogContentWrapper>
    </FormProvider>
  );
};

export default TalentAddWorkHistoryEntry;
